import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Camera, Plus, ArrowLeft, Tag, Clock, User, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';

const CATEGORIES = [
  'Privacy Violation',
  'Community Impact',
  'Legal Concern',
  'Personal Experience',
  'News & Updates',
  'Other'
];

export default function StoriesPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [showAddStory, setShowAddStory] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const [storyForm, setStoryForm] = useState({
    title: '',
    content: '',
    tags: '',
    category: '',
    image: null
  });

  useEffect(() => {
    loadStories();
  }, []);

  const loadStories = async () => {
    try {
      const res = await axios.get(`${API}/stories?status_filter=approved`);
      setStories(res.data);
    } catch (error) {
      console.error('Failed to load stories:', error);
    }
  };

  const handleAddStory = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append('title', storyForm.title);
    formData.append('content', storyForm.content);
    formData.append('tags', storyForm.tags);
    formData.append('category', storyForm.category);
    if (storyForm.image) {
      formData.append('image', storyForm.image);
    }

    try {
      await axios.post(`${API}/stories`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Story submitted! Awaiting moderator approval.');
      setShowAddStory(false);
      setStoryForm({
        title: '',
        content: '',
        tags: '',
        category: '',
        image: null
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit story');
    }
  };

  const filteredStories = selectedCategory === 'All' 
    ? stories 
    : stories.filter(s => s.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {/* Navigation */}
      <nav className="glass fixed top-0 left-0 right-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/')}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center space-x-2">
                <Camera className="w-8 h-8 text-blue-600" />
                <span className="text-2xl font-bold text-slate-800">FlockTracker</span>
              </div>
            </div>
            {user && (
              <Button 
                onClick={() => setShowAddStory(true)}
                data-testid="add-story-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Share Story
              </Button>
            )}
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-4">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 mb-4">Community Stories</h1>
          <p className="text-base sm:text-lg text-slate-600 mb-8">
            Read and share experiences about privacy, surveillance, and community impact.
          </p>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-8" data-testid="category-filter">
            <button
              onClick={() => setSelectedCategory('All')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === 'All'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              All
            </button>
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-slate-700 hover:bg-slate-100'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Stories Grid */}
          {filteredStories.length === 0 ? (
            <Card className="p-12 text-center">
              <p className="text-slate-600 text-lg">No stories yet. Be the first to share!</p>
            </Card>
          ) : (
            <div className="grid gap-6" data-testid="stories-grid">
              {filteredStories.map(story => (
                <Card 
                  key={story.id} 
                  className="story-card p-6 hover:shadow-lg cursor-pointer"
                  onClick={() => navigate(`/stories/${story.id}`)}
                  data-testid={`story-card-${story.id}`}
                >
                  <div className="flex flex-col md:flex-row gap-6">
                    {story.image_url && (
                      <div className="md:w-48 h-48 flex-shrink-0">
                        <img 
                          src={story.image_url} 
                          alt={story.title} 
                          className="w-full h-full object-cover rounded-lg"
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                          {story.category}
                        </span>
                        {story.tags.map(tag => (
                          <span key={tag} className="text-xs text-slate-500">#{tag}</span>
                        ))}
                      </div>
                      <h2 className="text-2xl font-bold text-slate-800 mb-2">{story.title}</h2>
                      <p className="text-slate-600 mb-4 line-clamp-3">{story.content}</p>
                      <div className="flex items-center gap-4 text-sm text-slate-500">
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {story.author_email}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {new Date(story.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Story Dialog */}
      <Dialog open={showAddStory} onOpenChange={setShowAddStory}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-story-dialog">
          <DialogHeader>
            <DialogTitle>Share Your Story</DialogTitle>
            <DialogDescription>
              Share your experience or insights about flock cameras and privacy. Your story will be reviewed before publication.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddStory} className="space-y-4">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                data-testid="story-title-input"
                value={storyForm.title}
                onChange={(e) => setStoryForm({ ...storyForm, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <Select 
                value={storyForm.category} 
                onValueChange={(value) => setStoryForm({ ...storyForm, category: value })}
              >
                <SelectTrigger data-testid="story-category-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="content">Story</Label>
              <Textarea
                id="content"
                rows={8}
                data-testid="story-content-input"
                value={storyForm.content}
                onChange={(e) => setStoryForm({ ...storyForm, content: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                id="tags"
                placeholder="e.g., privacy, surveillance, community"
                data-testid="story-tags-input"
                value={storyForm.tags}
                onChange={(e) => setStoryForm({ ...storyForm, tags: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="image">Image (optional)</Label>
              <Input
                id="image"
                type="file"
                accept="image/*"
                data-testid="story-image-input"
                onChange={(e) => setStoryForm({ ...storyForm, image: e.target.files[0] })}
              />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                Your story will be reviewed by a moderator before being published.
              </p>
            </div>
            <Button type="submit" className="w-full" data-testid="submit-story-btn">
              Submit Story
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}